import "./instrument";

import * as Sentry from "@sentry/react";
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App'
import { AuthProvider } from './contexts/AuthContext'
import { TourProvider } from './contexts/TourContext'
import { SeasonProvider } from './contexts/SeasonContext'
import { registerSW } from 'virtual:pwa-register'

// Auto-update the app after a deploy WITHOUT a manual hard refresh. registerType
// is 'autoUpdate', so registerSW installs/activates the new service worker and
// reloads open tabs automatically once it detects a new build. The catch:
// browsers only re-check the SW script on navigation, so a long-lived SPA tab
// (e.g. left open during a deploy — exactly Ryan's case) never notices. Polling
// registration.update() — and re-checking when the tab regains focus — makes the
// tab pick up the deploy within ~60s and reload itself.
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return
    const checkForUpdate = () => { registration.update().catch(() => {}) }
    setInterval(checkForUpdate, 60_000)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    })
  },
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

createRoot(document.getElementById('root')!, {
  // React 19 error handlers for Sentry
  onCaughtError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SeasonProvider>
            <TourProvider>
              <App />
            </TourProvider>
          </SeasonProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
